import React, { useState, useEffect } from 'react';
import { X, MapPin, Building, BedDouble, Bath, Users, Plus, Minus, Square } from 'lucide-react';
import { Property } from '../../types';

interface PropertyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Property>) => void;
  property: Property;
  missingFields?: string[];
  showAllFields?: boolean; // If true, show all fields regardless of missingFields
}

export const PropertyInfoModal: React.FC<PropertyInfoModalProps> = ({
  isOpen,
  onClose,
  onSave,
  property,
  missingFields = [],
  showAllFields = false
}) => {
  const getFullAddress = (prop: Property) => {
    const parts = [];
    if (prop.address) parts.push(prop.address);
    if (prop.zipCode) parts.push(prop.zipCode);
    if (prop.city) parts.push(prop.city);
    return parts.join(', ') || '';
  };

  const [formData, setFormData] = useState<Partial<Property>>({
    address: property.address || '',
    zipCode: property.zipCode || '',
    city: property.city || '',
    surface: property.surface || 0,
    bedrooms: property.bedrooms || 0,
    bathrooms: property.bathrooms || 0,
    maxGuests: property.maxGuests || 0,
  });

  // État pour l'affichage de l'adresse complète dans l'input
  const [displayAddress, setDisplayAddress] = useState(getFullAddress(property));

  useEffect(() => {
    if (isOpen) {
      setFormData({
        address: property.address || '',
        zipCode: property.zipCode || '',
        city: property.city || '',
        surface: property.surface || 0,
        bedrooms: property.bedrooms || 0,
        bathrooms: property.bathrooms || 0,
        maxGuests: property.maxGuests || 0,
      });
      setDisplayAddress(getFullAddress(property));
    }
  }, [isOpen, property]);

  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);

  const handleAddressSearch = async (query: string) => {
    setDisplayAddress(query);
    if (query.length > 3) {
      try {
        const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
        const data = await res.json();
        setAddressSuggestions(data.features || []);
      } catch (e) {
        console.error("Address API error", e);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const selectAddress = (feature: any) => {
    // Extraire séparément la rue, le code postal et la ville
    const streetAddress = feature.properties.name;
    const zipCode = feature.properties.postcode;
    const city = feature.properties.city;
    
    // Mettre à jour le formData avec les valeurs séparées
    setFormData({
      ...formData,
      address: streetAddress,
      zipCode: zipCode,
      city: city,
    });
    
    // Mettre à jour l'affichage
    setDisplayAddress(`${streetAddress}, ${zipCode} ${city}`);
    setAddressSuggestions([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const increment = (field: 'surface' | 'bedrooms' | 'bathrooms' | 'maxGuests') => {
    setFormData({
      ...formData,
      [field]: (formData[field] || 0) + 1,
    });
  };

  const decrement = (field: 'surface' | 'bedrooms' | 'bathrooms' | 'maxGuests') => {
    const current = formData[field] || 0;
    if (current > 0) {
      setFormData({
        ...formData,
        [field]: current - 1,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-slate-50">
          <h3 className="text-lg font-bold text-slate-900 flex items-center">
            <Building className="w-5 h-5 mr-2 text-amber-500" />
            {showAllFields ? 'Modifier les informations du bien' : 'Informations du bien requises'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {!showAllFields && (
            <p className="text-sm text-slate-600 mb-6 p-3 bg-amber-50 rounded-lg border border-amber-100">
              Pour générer une estimation précise, veuillez compléter les informations suivantes :
            </p>
          )}

          <div className="space-y-6">
            {/* Adresse - Pleine largeur */}
            {(showAllFields || missingFields.includes('Adresse')) && (
              <div className="relative">
                <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-amber-500" />
                  Adresse complète *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required={showAllFields || missingFields.includes('Adresse')}
                    className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-900 transition-all"
                    placeholder="Ex: 123 Rue de la République, 69001 Lyon"
                    value={displayAddress}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                  />
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
                {addressSuggestions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border-2 border-slate-200 rounded-lg mt-1 shadow-xl max-h-48 overflow-auto">
                    {addressSuggestions.map((s, i) => (
                      <li key={i} onClick={() => selectAddress(s)} className="p-3 hover:bg-amber-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900 transition-colors">
                        <span className="font-semibold">{s.properties.name}</span> <span className="text-slate-500">{s.properties.postcode} {s.properties.city}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Grid pour les champs numériques - 2 colonnes */}
            <div className="grid grid-cols-2 gap-4">
              {/* Surface */}
              {(showAllFields || missingFields.includes('Surface')) && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Square className="w-4 h-4 mr-2 text-amber-500" />
                    Surface (m²) *
                  </label>
                  <div className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-amber-500 transition-all">
                    <button
                      type="button"
                      onClick={() => decrement('surface')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-r border-slate-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      required={showAllFields || missingFields.includes('Surface')}
                      className="flex-1 px-4 py-2.5 text-center font-semibold text-slate-900 focus:outline-none bg-white"
                      value={formData.surface || ''}
                      onChange={(e) => setFormData({ ...formData, surface: Number(e.target.value) || 0 })}
                    />
                    <button
                      type="button"
                      onClick={() => increment('surface')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-l border-slate-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Chambres */}
              {(showAllFields || missingFields.includes('Chambres')) && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <BedDouble className="w-4 h-4 mr-2 text-amber-500" />
                    Chambres *
                  </label>
                  <div className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-amber-500 transition-all">
                    <button
                      type="button"
                      onClick={() => decrement('bedrooms')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-r border-slate-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      required={showAllFields || missingFields.includes('Chambres')}
                      className="flex-1 px-4 py-2.5 text-center font-semibold text-slate-900 focus:outline-none bg-white"
                      value={formData.bedrooms || ''}
                      onChange={(e) => setFormData({ ...formData, bedrooms: Number(e.target.value) || 0 })}
                    />
                    <button
                      type="button"
                      onClick={() => increment('bedrooms')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-l border-slate-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Salles de bain */}
              {(showAllFields || missingFields.includes('Salles de bain')) && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Bath className="w-4 h-4 mr-2 text-amber-500" />
                    Salles de bain *
                  </label>
                  <div className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-amber-500 transition-all">
                    <button
                      type="button"
                      onClick={() => decrement('bathrooms')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-r border-slate-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      required={showAllFields || missingFields.includes('Salles de bain')}
                      className="flex-1 px-4 py-2.5 text-center font-semibold text-slate-900 focus:outline-none bg-white"
                      value={formData.bathrooms || ''}
                      onChange={(e) => setFormData({ ...formData, bathrooms: Number(e.target.value) || 0 })}
                    />
                    <button
                      type="button"
                      onClick={() => increment('bathrooms')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-l border-slate-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Voyageurs */}
              {(showAllFields || missingFields.includes('Capacité max voyageurs')) && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-amber-500" />
                    Capacité max voyageurs *
                  </label>
                  <div className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden bg-white focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-amber-500 transition-all">
                    <button
                      type="button"
                      onClick={() => decrement('maxGuests')}
                      disabled={(formData.maxGuests || 0) <= 1}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-r border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      required={showAllFields || missingFields.includes('Capacité max voyageurs')}
                      className="flex-1 px-4 py-2.5 text-center font-semibold text-slate-900 focus:outline-none bg-white"
                      value={formData.maxGuests || ''}
                      onChange={(e) => setFormData({ ...formData, maxGuests: Number(e.target.value) || 1 })}
                    />
                    <button
                      type="button"
                      onClick={() => increment('maxGuests')}
                      className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold transition-colors border-l border-slate-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-6 mt-6 flex justify-end space-x-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-semibold shadow-lg shadow-amber-500/30"
            >
              {showAllFields ? 'Enregistrer' : 'Enregistrer et estimer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
